import { useMutation, useQuery } from "@apollo/client/react";
import { Loader, Select, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ResultOf, graphql } from "gql.tada";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { CreationForm } from "../../components/form/CreationForm";

const EMAIL_CONFIGURATION_QUERY = graphql(`
  query EmailConfiguration {
    emailConfiguration {
      emailField {
        property
        propertySource
        propertySourceId
      }
      username
      password
      host
      port
      from
      fromName
      replyTo
      replyToName
      secure
    }
  }
`);

const UPDATE_EMAIL_CONFIGURATION_MUTATION = graphql(`
  mutation UpdateEmailConfiguration($input: EmailConfigurationInput!) {
    emailConfigurationUpdate(input: $input) {
      from
    }
  }
`);

const IDENTITY_PROVIDERS_QUERY = graphql(`
  query IdentityProviders {
    identityProviders {
      ... on OrganizationIdentityProvider {
        id
        name
        configuration {
          ... on OAuthClientConfiguration {
            dataRetrievers {
              mappingConfiguration {
                mappings {
                  mappedTo
                }
              }
            }
          }
        }
      }
    }
  }
`);

const PropertySourceConfig = ({
  propertySource,
  propertySourceId,
  property,
  identityProviders,
  onChange,
}: {
  propertySource: "IDENTITY_PROVIDER" | "PLAYER";
  propertySourceId?: string;
  property: string;
  identityProviders: ResultOf<
    typeof IDENTITY_PROVIDERS_QUERY
  >["identityProviders"];
  onChange: (propertySourceConfig: {
    property: string;
    propertySourceId?: string;
    propertySource: "IDENTITY_PROVIDER" | "PLAYER";
  }) => void;
}) => {
  const [propertySourceValue, setPropertySourceValue] =
    useState(propertySource);
  const [propertySourceIdValue, setPropertySourceIdValue] =
    useState(propertySourceId);
  const [propertyValue, setPropertyValue] = useState(property);

  useEffect(() => {
    onChange({
      property: propertyValue,
      propertySourceId: propertySourceIdValue,
      propertySource: propertySourceValue,
    });
  }, [propertySourceValue, propertySourceIdValue, propertyValue]);

  return (
    <>
      <Select
        label="Property Source"
        placeholder="Select a property source"
        data={[
          {
            label: "Identity Provider",
            value: "IDENTITY_PROVIDER",
          },
          {
            label: "Player",
            value: "PLAYER",
          },
        ]}
        value={propertySourceValue}
        onChange={(value) => {
          setPropertySourceValue(value as "IDENTITY_PROVIDER" | "PLAYER");
        }}
      />
      {propertySourceValue === "IDENTITY_PROVIDER" && (
        <>
          <Select
            label="Identity Provider"
            placeholder="Select an identity provider"
            data={identityProviders.map((idp) => ({
              label:
                idp.__typename === "OrganizationIdentityProvider"
                  ? idp.name
                  : "",
              value:
                idp.__typename === "OrganizationIdentityProvider" ? idp.id : "",
            }))}
            value={propertySourceIdValue}
            onChange={(value) => {
              setPropertySourceIdValue(value ?? undefined);
            }}
          />
          {propertySourceIdValue &&
            (() => {
              const idp = identityProviders.find(
                (idp) =>
                  idp.__typename === "OrganizationIdentityProvider" &&
                  idp.id === propertySourceIdValue,
              );

              return (
                <Select
                  label="Property"
                  placeholder="Select a property"
                  data={
                    (idp?.__typename === "OrganizationIdentityProvider" &&
                    idp.configuration?.__typename === "OAuthClientConfiguration"
                      ? idp.configuration?.dataRetrievers.flatMap(
                          (dataRetriever) =>
                            dataRetriever.mappingConfiguration?.mappings.map(
                              (mapping) => ({
                                label: mapping.mappedTo,
                                value: mapping.mappedTo,
                              }),
                            ),
                        )
                      : []) ?? []
                  }
                  value={propertyValue}
                  onChange={(value) => {
                    setPropertyValue(value ?? "");
                  }}
                />
              );
            })()}
        </>
      )}

      {propertySourceValue === "PLAYER" && (
        <TextInput
          label="Property"
          placeholder="Enter a property"
          value={propertyValue}
          onChange={(event) => {
            setPropertyValue(event.target.value);
          }}
        />
      )}
    </>
  );
};

export const EmailConfiguration = () => {
  const { data, loading } = useQuery<any>(EMAIL_CONFIGURATION_QUERY);
  const { loading: idpLoading, data: idpData } = useQuery<any>(
    IDENTITY_PROVIDERS_QUERY,
  );
  const [updateEmailConfiguration] = useMutation<any>(
    UPDATE_EMAIL_CONFIGURATION_MUTATION,
  );

  if (loading || idpLoading) return <Loader />;

  return (
    <CreationForm
      title="Email Configuration"
      submitText="Save and test SMTP connection"
      schemaDefinition={{
        host: {
          type: "INPUT",
          label: "Host",
          yupConfig: yup.string().required(),
          inputType: "text",
          defaultValue: data?.emailConfiguration?.host,
          placeholder: "SMTP server address",
        },
        port: {
          type: "INPUT",
          label: "Port",
          yupConfig: yup.number(),
          inputType: "number",
          defaultValue: data?.emailConfiguration?.port,
          placeholder: "SMTP server port (optional)",
        },
        username: {
          type: "INPUT",
          label: "Username",
          yupConfig: yup.string(),
          inputType: "text",
          defaultValue: data?.emailConfiguration?.username,
          placeholder: "Username for the email SMTP server (optional)",
        },
        password: {
          type: "INPUT",
          label: "Password",
          yupConfig: yup.string(),
          inputType: "password",
          defaultValue: data?.emailConfiguration?.password,
          placeholder: "Password for the email SMTP server (optional)",
        },
        from: {
          type: "INPUT",
          label: "From",
          yupConfig: yup.string(),
          inputType: "email",
          defaultValue: data?.emailConfiguration?.from,
          placeholder: "From email address (optional)",
        },
        fromName: {
          type: "INPUT",
          label: "From Name",
          yupConfig: yup.string(),
          inputType: "text",
          defaultValue: data?.emailConfiguration?.fromName,
          placeholder: "From name (optional)",
        },
        replyTo: {
          type: "INPUT",
          label: "Reply To",
          yupConfig: yup.string(),
          inputType: "email",
          defaultValue: data?.emailConfiguration?.replyTo,
          placeholder: "Reply to email address (optional)",
        },
        replyToName: {
          type: "INPUT",
          label: "Reply To Name",
          yupConfig: yup.string(),
          inputType: "text",
          defaultValue: data?.emailConfiguration?.replyToName,
          placeholder: "Reply to name (optional)",
        },
        secure: {
          type: "CHECKBOX",
          label: "Secure",
          yupConfig: yup.boolean().required(),
          defaultValue: data?.emailConfiguration?.secure === true,
        },
        emailField: {
          type: "DYNAMIC",
          label: "Email Field",
          yupConfig: yup
            .object()
            .shape({
              property: yup.string().required(),
              propertySource: yup
                .string()
                .oneOf(["IDENTITY_PROVIDER", "PLAYER"])
                .required(),
              propertySourceId: yup.string(),
            })
            .required(),
          element: (onChange) => (
            <PropertySourceConfig
              propertySource={
                data?.emailConfiguration?.emailField?.propertySource ?? "PLAYER"
              }
              propertySourceId={
                data?.emailConfiguration?.emailField?.propertySourceId ??
                undefined
              }
              property={data?.emailConfiguration?.emailField?.property ?? ""}
              identityProviders={idpData?.identityProviders ?? []}
              onChange={onChange}
            />
          ),
        },
      }}
      onSubmit={async (values) => {
        await updateEmailConfiguration({
          variables: {
            input: values,
          },
        }).then((data) => {
          if (data?.error) {
            throw new Error(data.error.message);
          }
        });
        notifications.show({
          title: "Email configuration updated",
          message: "The email configuration has been updated",
          color: "green",
          autoClose: 3000,
        });
      }}
    />
  );
};
